import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Loader2, ExternalLink, ShieldCheck, ArrowLeft, RefreshCw } from "lucide-react";
import { isNativeBilling, openStoreSubscriptions, storeName } from "../lib/billing";

export const BillingPortal = () => {
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const navigate = useNavigate();
  const native = isNativeBilling();

  const openPortal = useCallback(async () => {
    // Sur mobile natif, l'abonnement est géré par le store (StoreKit / Play Billing).
    if (isNativeBilling()) {
      openStoreSubscriptions();
      setStatus("idle");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      // Récupère le token de session (si présent)
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;

      const res = await fetch("/.netlify/functions/create-portal-session", {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });

      const text = await res.text();
      let data: any = {};
      try { data = JSON.parse(text); } catch { /* ignore parsing error */ }

      if (res.ok && data?.url) {
        // Redirection immédiate vers le portail Stripe
        window.location.href = data.url;
        return;
      }

      // Erreur contrôlée
      setStatus("error");
      setErrorMsg(data?.message || text || "Portail indisponible pour le moment.");
    } catch (e: any) {
      setStatus("error");
      setErrorMsg(e?.message || "Une erreur est survenue.");
    }
  }, []);

  useEffect(() => {
    // au montage, on tente l’ouverture automatique
    openPortal();
  }, [openPortal]);

  return (
    <section className="min-h-[70vh] bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <div className="mx-auto max-w-lg px-4 py-14">
        <div className="rounded-2xl border border-white/40 bg-white/70 p-8 text-center shadow-lg backdrop-blur-md dark:border-white/5 dark:bg-white/5">
          <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-600/10">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold">Gestion de l’abonnement</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {native
              ? `Ton abonnement est géré via ${storeName()}. On ouvre la page de gestion de ton compte.`
              : "Redirection vers le portail sécurisé Stripe pour gérer ton abonnement, tes factures et tes moyens de paiement."}
          </p>

          {native && (
            <div className="mt-8">
              <button
                onClick={openStoreSubscriptions}
                className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"
              >
                <ExternalLink className="h-4 w-4" />
                Ouvrir la gestion des abonnements
              </button>
            </div>
          )}

          {!native && status !== "error" && (
            <div className="mt-8 flex flex-col items-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Ouverture du portail en cours…
              </p>
            </div>
          )}

          {status === "error" && (
            <div className="mt-8 rounded-lg border border-red-300/40 bg-red-50/70 p-4 text-left dark:border-red-400/20 dark:bg-red-900/20">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                {errorMsg}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={openPortal}
                  className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"
                >
                  <RefreshCw className="h-4 w-4" />
                  Réessayer
                </button>
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white/70 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Retour
                </button>
              </div>
            </div>
          )}

          <div className="mt-8 text-xs text-gray-500">
            Besoin d’aide ? <Link to="/contact" className="text-orange-600 hover:underline">Contacte-nous</Link>.
          </div>

          <div className="mt-4">
            <a
              href="https://stripe.com/docs/billing/subscriptions/customer-portal"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:underline"
            >
              En savoir plus sur le portail <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BillingPortal;
